/**
 * useGraphPrincipals — batched GUID → display-name resolution via Microsoft Graph.
 *
 * Otto ARM responses identify principals (e.g. `properties.createdBy`,
 * `properties.updatedBy`) as raw AAD object IDs (GUIDs). The portal renders
 * these as human-readable names by batch-resolving against
 * `directoryObjects/getByIds`, which returns the principal regardless of type
 * (user, group, application, managed identity).
 *
 * Results are cached for 5 minutes per stable id set so the same group of GUIDs
 * does not re-fetch within a session.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { GUID_PATTERN } from '@/utils/primitives';

export interface GraphPrincipal {
  id: string;
  displayName: string;
  /** Email when present (Graph `mail`). Empty string when the directory object has no mail. */
  email: string;
  userPrincipalName: string;
  type: 'user' | 'group' | 'application' | 'servicePrincipal' | 'unknown';
}

export type GraphPrincipalMap = Record<string, GraphPrincipal>;

const GRAPH_DIRECTORY_OBJECTS_BY_IDS = 'https://graph.microsoft.com/v1.0/directoryObjects/getByIds';

const isLocalOrE2E = (): boolean =>
  import.meta.env.VITE_E2E_MODE === 'true' || import.meta.env.VITE_LOCAL_MODE === 'true';

const dedupedGuids = (ids: ReadonlyArray<string | null | undefined>): string[] => {
  const out = new Set<string>();
  for (const raw of ids) {
    if (!raw) continue;
    const trimmed = raw.trim();
    if (trimmed.length === 0) continue;
    if (!GUID_PATTERN.test(trimmed)) continue;
    out.add(trimmed.toLowerCase());
  }
  return Array.from(out).sort();
};

function odataTypeToKind(value: string | undefined): GraphPrincipal['type'] {
  if (!value) return 'unknown';
  // `@odata.type` looks like `#microsoft.graph.user` / `#microsoft.graph.group` etc.
  const tail = value.split('.').pop()?.toLowerCase();
  switch (tail) {
    case 'user':
      return 'user';
    case 'group':
      return 'group';
    case 'application':
      return 'application';
    case 'serviceprincipal':
      return 'servicePrincipal';
    default:
      return 'unknown';
  }
}

/**
 * Resolve one or more AAD object IDs to display-friendly principals via Graph.
 *
 * - Non-GUID values, empty strings, and duplicates are stripped before the call.
 * - In local / E2E mode the hook short-circuits and returns an empty map (no
 *   Graph token is available in those environments).
 * - Returns a `Record<lowercased-guid, GraphPrincipal>`; callers should look up
 *   `map[guid.toLowerCase()]`.
 */
export function useGraphPrincipals(ids: ReadonlyArray<string | null | undefined>) {
  const { isAuthenticated, account, getGraphToken } = useAuth();
  // Tenant-scope the cache key so a previous account's resolution can never bleed
  // through after the user switches AAD context (Graph object IDs are tenant-scoped).
  const accountId = account?.localAccountId;
  const tenantId = account?.tenantId;

  const requestIds = useMemo(() => dedupedGuids(ids), [ids]);
  const cacheKey = requestIds.join(',');

  // Stable, fully-qualified scope segment. When auth isn't ready we use an explicit
  // 'anonymous' label so a future authenticated key with the same id set can never
  // alias the same React Query cache slot.
  const scope = accountId && tenantId
    ? 'auth:' + tenantId.toLowerCase() + ':' + accountId.toLowerCase()
    : 'anonymous';
  const queryKey = useMemo(() => ['graphPrincipals', scope, cacheKey] as const, [scope, cacheKey]);

  return useQuery({
    queryKey,
    queryFn: async (): Promise<GraphPrincipalMap> => {
      if (requestIds.length === 0) return {};
      if (isLocalOrE2E()) return {};

      const token = await getGraphToken();
      if (!token) {
        // Caller sees an empty map; they will fall back to the raw GUID display.
        return {};
      }

      const response = await fetch(GRAPH_DIRECTORY_OBJECTS_BY_IDS, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: requestIds,
          types: ['user', 'group', 'application', 'servicePrincipal'],
        }),
      });

      if (!response.ok) {
        throw new Error(`Graph getByIds failed: ${response.status}`);
      }

      const body = (await response.json()) as { value?: Array<Record<string, unknown>> };
      const map: GraphPrincipalMap = {};
      for (const entry of body.value ?? []) {
        const id = typeof entry.id === 'string' ? entry.id.toLowerCase() : '';
        if (!id) continue;
        map[id] = {
          id,
          displayName: typeof entry.displayName === 'string' ? entry.displayName : '',
          email: typeof entry.mail === 'string' ? entry.mail : '',
          userPrincipalName: typeof entry.userPrincipalName === 'string' ? entry.userPrincipalName : '',
          type: odataTypeToKind(typeof entry['@odata.type'] === 'string' ? (entry['@odata.type'] as string) : undefined),
        };
      }
      return map;
    },
    enabled: requestIds.length > 0 && (isLocalOrE2E() || (isAuthenticated && !!accountId)),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}